// This workaround fixes a bug in chart.js where the alignment of tooltip items is wrong when forcing the position with only yAlign set
// Chart.js miscalculates the yAlign property in its code which is difficult to customize so we have to reimplement the entire updat() method
// This method adds a new property yAlignForced to the tooltip config (affects getBaseModel) and this is used to skip the bounds check.
import Chart from 'chart.js';

const defaults = Chart.defaults;
const helpers = Chart.helpers;

// Private helper to create a tooltip item model
// @param element : the chart element (point, arc, bar) to create the tooltip item for
// @return : new tooltip item
function createTooltipItem(element) {
    let xScale = element._xScale;
    let yScale = element._yScale || element._scale; // handle radar || polarArea charts
    let index = element._index;
    let datasetIndex = element._datasetIndex;

    return {
        xLabel: xScale ? xScale.getLabelForIndex(index, datasetIndex) : '',
        yLabel: yScale ? yScale.getLabelForIndex(index, datasetIndex) : '',
        index: index,
        datasetIndex: datasetIndex,
        x: element._model.x,
        y: element._model.y
    };
}

/**
 * Helper to get the reset model for the tooltip
 * @param tooltipOpts {Object} the tooltip options
 */
function getBaseModel(tooltipOpts) {
    let globalDefaults = defaults.global;
    let valueOrDefault = helpers.valueOrDefault;

    return {
        // Positioning
        xPadding: tooltipOpts.xPadding,
        yPadding: tooltipOpts.yPadding,
        xAlign: tooltipOpts.xAlign,
        yAlign: tooltipOpts.yAlign,
        yAlignForced: tooltipOpts.yAlignForced,

        // Body
        bodyFontColor: tooltipOpts.bodyFontColor,
        _bodyFontFamily: valueOrDefault(tooltipOpts.bodyFontFamily, globalDefaults.defaultFontFamily),
        _bodyFontStyle: valueOrDefault(tooltipOpts.bodyFontStyle, globalDefaults.defaultFontStyle),
        _bodyAlign: tooltipOpts.bodyAlign,
        bodyFontSize: valueOrDefault(tooltipOpts.bodyFontSize, globalDefaults.defaultFontSize),
        bodySpacing: tooltipOpts.bodySpacing,

        // Title
        titleFontColor: tooltipOpts.titleFontColor,
        _titleFontFamily: valueOrDefault(tooltipOpts.titleFontFamily, globalDefaults.defaultFontFamily),
        _titleFontStyle: valueOrDefault(tooltipOpts.titleFontStyle, globalDefaults.defaultFontStyle),
        titleFontSize: valueOrDefault(tooltipOpts.titleFontSize, globalDefaults.defaultFontSize),
        _titleAlign: tooltipOpts.titleAlign,
        titleSpacing: tooltipOpts.titleSpacing,
        titleMarginBottom: tooltipOpts.titleMarginBottom,

        // Footer
        footerFontColor: tooltipOpts.footerFontColor,
        _footerFontFamily: valueOrDefault(tooltipOpts.footerFontFamily, globalDefaults.defaultFontFamily),
        _footerFontStyle: valueOrDefault(tooltipOpts.footerFontStyle, globalDefaults.defaultFontStyle),
        footerFontSize: valueOrDefault(tooltipOpts.footerFontSize, globalDefaults.defaultFontSize),
        _footerAlign: tooltipOpts.footerAlign,
        footerSpacing: tooltipOpts.footerSpacing,
        footerMarginTop: tooltipOpts.footerMarginTop,

        // Appearance
        caretSize: tooltipOpts.caretSize,
        cornerRadius: tooltipOpts.cornerRadius,
        backgroundColor: tooltipOpts.backgroundColor,
        opacity: 0,
        legendColorBackground: tooltipOpts.multiKeyBackground,
        displayColors: tooltipOpts.displayColors,
        borderColor: tooltipOpts.borderColor,
        borderWidth: tooltipOpts.borderWidth
    };
}

/**
 * Get the size of the tooltip
 */
function getTooltipSize(tooltip, model) {
    let ctx = tooltip._chart.ctx;

    let height = model.yPadding * 2; // Tooltip Padding
    let width = 0;

    // Count of all lines in the body
    let body = model.body;
    let combinedBodyLength = body.reduce(function(count, bodyItem) {
        return count + bodyItem.before.length + bodyItem.lines.length + bodyItem.after.length;
    }, 0);
    combinedBodyLength += model.beforeBody.length + model.afterBody.length;

    let titleLineCount = model.title.length;
    let footerLineCount = model.footer.length;
    let titleFontSize = model.titleFontSize;
    let bodyFontSize = model.bodyFontSize;
    let footerFontSize = model.footerFontSize;

    height += titleLineCount * titleFontSize; // Title Lines
    height += titleLineCount ? (titleLineCount - 1) * model.titleSpacing : 0; // Title Line Spacing
    height += titleLineCount ? model.titleMarginBottom : 0; // Title's bottom Margin
    height += combinedBodyLength * bodyFontSize; // Body Lines
    height += combinedBodyLength ? (combinedBodyLength - 1) * model.bodySpacing : 0; // Body Line Spacing
    height += footerLineCount ? model.footerMarginTop : 0; // Footer Margin
    height += footerLineCount * (footerFontSize); // Footer Lines
    height += footerLineCount ? (footerLineCount - 1) * model.footerSpacing : 0; // Footer Line Spacing

    // Title width
    let widthPadding = 0;
    let maxLineWidth = function(line) {
        width = Math.max(width, ctx.measureText(line).width + widthPadding);
    };

    ctx.font = helpers.fontString(titleFontSize, model._titleFontStyle, model._titleFontFamily);
    helpers.each(model.title, maxLineWidth);

    // Body width
    ctx.font = helpers.fontString(bodyFontSize, model._bodyFontStyle, model._bodyFontFamily);
    helpers.each(model.beforeBody.concat(model.afterBody), maxLineWidth);

    // Body lines may include some extra width due to the color box
    widthPadding = model.displayColors ? (bodyFontSize + 2) : 0;
    helpers.each(body, function(bodyItem) {
        helpers.each(bodyItem.before, maxLineWidth);
        helpers.each(bodyItem.lines, maxLineWidth);
        helpers.each(bodyItem.after, maxLineWidth);
    });

    // Reset back to 0
    widthPadding = 0;

    // Footer width
    ctx.font = helpers.fontString(footerFontSize, model._footerFontStyle, model._footerFontFamily);
    helpers.each(model.footer, maxLineWidth);

    // Add padding
    width += 2 * model.xPadding;

    return {
        width: width,
        height: height
    };
}

/**
 * Helper to get the alignment of a tooltip given the size
 */
function determineAlignment(tooltip, size) {
    let model = tooltip._model;
    let chart = tooltip._chart;
    let chartArea = tooltip._chart.chartArea;
    let xAlign = 'center';
    let yAlign = 'center';

    if (!tooltip._options.yAlignForced) {
        if (model.y < size.height) {
            yAlign = 'top';
        } else if (model.y > (chart.height - size.height)) {
            yAlign = 'bottom';
        }
    } else {
        yAlign = tooltip._options.yAlign || 'center';
    }

    let lf, rf; // functions to determine left, right alignment
    let olf, orf; // functions to determine if left/right alignment causes tooltip to go outside chart
    let yf; // function to get the y alignment if the tooltip goes outside of the left or right edges
    let midX = (chartArea.left + chartArea.right) / 2;
    let midY = (chartArea.top + chartArea.bottom) / 2;

    if (yAlign === 'center') {
        lf = function(x) {
            return x <= midX;
        };
        rf = function(x) {
            return x > midX;
        };
    } else {
        lf = function(x) {
            return x <= (size.width / 2);
        };
        rf = function(x) {
            return x >= (chart.width - (size.width / 2));
        };
    }

    olf = function(x) {
        return x + size.width + model.caretSize + model.caretPadding > chart.width;
    };
    orf = function(x) {
        return x - size.width - model.caretSize - model.caretPadding < 0;
    };
    yf = function(y) {
        return y <= midY ? 'top' : 'bottom';
    };

    if (lf(model.x)) {
        xAlign = 'left';

        // Is tooltip too wide and goes over the right side of the chart.?
        if (olf(model.x)) {
            xAlign = 'center';
            yAlign = yf(model.y);
        }
    } else if (rf(model.x)) {
        xAlign = 'right';

        // Is tooltip too wide and goes outside left edge of canvas?
        if (orf(model.x)) {
            xAlign = 'center';
            yAlign = yf(model.y);
        }
    }

    let opts = tooltip._options;
    return {
        xAlign: opts.xAlign ? opts.xAlign : xAlign,
        yAlign: opts.yAlign ? opts.yAlign : yAlign
    };
}

/**
 * Helper to get the location a tooltip needs to be placed at given the initial position (via the vm) and the size and alignment
 */
function getBackgroundPoint(vm, size, alignment, chart) {
    // Background Position
    let x = vm.x;
    let y = vm.y;

    let caretSize = vm.caretSize;
    let caretPadding = vm.caretPadding;
    let cornerRadius = vm.cornerRadius;
    let xAlign = alignment.xAlign;
    let yAlign = alignment.yAlign;
    let paddingAndSize = caretSize + caretPadding;
    let radiusAndPadding = cornerRadius + caretPadding;

    if (xAlign === 'right') {
        x -= size.width;
    } else if (xAlign === 'center') {
        x -= (size.width / 2);
        if (x + size.width > chart.width) {
            x = chart.width - size.width;
        }
        if (x < 0) {
            x = 0;
        }
    }

    if (yAlign === 'top') {
        y += paddingAndSize;
    } else if (yAlign === 'bottom') {
        y -= size.height + paddingAndSize;
    } else {
        y -= (size.height / 2);
    }

    if (yAlign === 'center') {
        if (xAlign === 'left') {
            x += paddingAndSize;
        } else if (xAlign === 'right') {
            x -= paddingAndSize;
        }
    } else if (xAlign === 'left') {
        x -= radiusAndPadding;
    } else if (xAlign === 'right') {
        x += radiusAndPadding;
    }

    return {
        x: x,
        y: y
    };
}

function fixedUpdate(changed) {
    let me = this;
    let opts = me._options;

    // Need to regenerate the model because its faster than using extend and it is necessary due to the optimization in Chart.Element.transition
    // that does _view = _model if ease === 1. This causes the 2nd tooltip update to set properties in both the view and model at the same time
    // which breaks any animations.
    let existingModel = me._model;
    let model = me._model = getBaseModel(opts);
    let active = me._active;

    let data = me._data;

    // In the case where active.length === 0 we need to keep these at existing values for good animations
    let alignment = {
        xAlign: existingModel.xAlign,
        yAlign: existingModel.yAlign
    };
    let backgroundPoint = {
        x: existingModel.x,
        y: existingModel.y
    };
    let tooltipSize = {
        width: existingModel.width,
        height: existingModel.height
    };
    let tooltipPosition = {
        x: existingModel.caretX,
        y: existingModel.caretY
    };

    let i, len;

    if (active.length) {
        model.opacity = 1;

        let labelColors = [];
        let labelTextColors = [];
        tooltipPosition = Chart.Tooltip.positioners[opts.position].call(me, active, me._eventPosition);

        let tooltipItems = [];
        for (i = 0, len = active.length; i < len; ++i) {
            tooltipItems.push(createTooltipItem(active[i]));
        }

        // If the user provided a filter function, use it to modify the tooltip items
        if (opts.filter) {
            tooltipItems = tooltipItems.filter(function(a) {
                return opts.filter(a, data);
            });
        }

        // If the user provided a sorting function, use it to modify the tooltip items
        if (opts.itemSort) {
            tooltipItems = tooltipItems.sort(function(a, b) {
                return opts.itemSort(a, b, data);
            });
        }

        // Determine colors for boxes
        helpers.each(tooltipItems, function(tooltipItem) {
            labelColors.push(opts.callbacks.labelColor.call(me, tooltipItem, me._chart));
            labelTextColors.push(opts.callbacks.labelTextColor.call(me, tooltipItem, me._chart));
        });


        // Build the Text Lines
        model.title = me.getTitle(tooltipItems, data);
        model.beforeBody = me.getBeforeBody(tooltipItems, data);
        model.body = me.getBody(tooltipItems, data);
        model.afterBody = me.getAfterBody(tooltipItems, data);
        model.footer = me.getFooter(tooltipItems, data);

        // Initial positioning and colors
        model.x = Math.round(tooltipPosition.x);
        model.y = Math.round(tooltipPosition.y);
        model.caretPadding = opts.caretPadding;
        model.labelColors = labelColors;
        model.labelTextColors = labelTextColors;

        // data points
        model.dataPoints = tooltipItems;

        // We need to determine alignment of the tooltip
        tooltipSize = getTooltipSize(this, model);
        alignment = determineAlignment(this, tooltipSize);
        // Final Size and Position
        backgroundPoint = getBackgroundPoint(model, tooltipSize, alignment, me._chart);
    } else {
        model.opacity = 0;
    }

    model.xAlign = alignment.xAlign;
    model.yAlign = alignment.yAlign;
    model.x = backgroundPoint.x;
    model.y = backgroundPoint.y;
    model.width = tooltipSize.width;
    model.height = tooltipSize.height;

    // Point where the caret on the tooltip points to
    model.caretX = tooltipPosition.x;
    model.caretY = tooltipPosition.y;

    me._model = model;

    if (changed && opts.custom) {
        opts.custom.call(me, model);
    }

    return me;
}
Chart.Tooltip.prototype.update = fixedUpdate;



// WEBPACK FOOTER //
// ./src/components/widgets/tracker-graph/workaround.js